import { ChatState, ConnectionState } from '../../core/models/events-models';
import { Provider } from '../provider-abstract';
export class GatewayProvider extends Provider {
    constructor() {
        super(...arguments);
        this.eventMapping = {
            newMessage: this._newMessage,
            participantUpdate: this._participantUpdate,
            featureUpdate: this._featureUpdate,
            isTyping: this._isTyping,
            pong: this.signalPongReceived,
            error: this._error,
            statusChange: this._statusChange,
            acknowledgement: this._acknowledgement,
            notification: this._notification,
        };
    }
    sendIsTypingHandler() {
        this.sendMessageInternal({
            event: 'isTyping',
        });
    }
    sendPing() {
        this.sendMessageInternal({
            event: 'ping',
        });
    }
    sendRequestChat(token, provider, platform, request) {
        this.sendMessageInternal({
            event: 'requestChat',
            data: {
                accessToken: token,
                providerId: provider,
                sdkVersion: '3.0',
                platformType: platform,
                requestParameters: request,
            },
        });
    }
    sendRestoreChat(token, sessionId) {
        this.sendMessageInternal({
            event: 'restoreChat',
            data: {
                accessToken: token,
                sessionId: sessionId,
            },
        });
    }
    _sendAcknowledgement(messageId) {
        this.sendMessageInternal({
            event: 'acknowledgement',
            data: {
                messageId: messageId,
            },
        });
    }
    onChatClose(message) {
        this.sendMessageInternal({
            event: 'endChat',
            data: message
                ? {
                    errorMessage: message,
                }
                : undefined,
        });
    }
    webSocketMessageHandler(json) {
        const msg = JSON.parse(json.data);
        const eventName = msg === null || msg === void 0 ? void 0 : msg.event;
        const eventHandler = this.eventMapping[eventName];
        if (!eventHandler) {
            if (this.session.config.useMocks.ignoreBadMessage) {
            }
            else {
                this.session.chatInstance.emitError('MESSAGE_RECEIVE_FAILURE', 'Incoming message type could not be processed: ' + eventName.toString());
            }
            return;
        }
        eventHandler.bind(this)(msg.data);
    }
    _newMessage(data) {
        var _a;
        const msg = data;
        if (msg && msg.messageId && msg.participant && msg.messages) {
            this._sendAcknowledgement(msg.messageId);
            this.session.chatInstance.emitNewMessage((_a = msg.provider) !== null && _a !== void 0 ? _a : this.providerIdentifier, msg.participant, msg.messages, new Date());
        }
        else {
            this.session.chatInstance.emitError('MESSAGE_RECEIVE_FAILURE', `Bad message. ${JSON.stringify(msg)}`);
        }
    }
    _participantUpdate(data) {
        let updateInfo = data;
        if (updateInfo && updateInfo.provider && updateInfo.participant && updateInfo.action && updateInfo.status) {
            this.session.chatInstance.emitParticipantUpdate(updateInfo.provider, updateInfo.participant, updateInfo.action, updateInfo.status, new Date());
        }
        else {
            this.session.chatInstance.emitError('PARTICIPANT_UPDATE_FAILURE');
        }
    }
    _featureUpdate(data) {
        let updateInfo = data;
        if (updateInfo && updateInfo.features && updateInfo.gatewaySettings) {
            this.session.chatInstance.emitFeatureUpdate(updateInfo.features, updateInfo.gatewaySettings);
            this.setPingInterval();
        }
        else {
            this.session.chatInstance.emitError('FEATURE_UPDATE_FAILURE');
        }
    }
    _isTyping(data) {
        const typing = data;
        if (typing && typing.participant) {
            this.session.chatInstance.emitIsTyping(typing.participant, new Date());
        }
        else {
            this.session.chatInstance.emitError('MESSAGE_RECEIVE_FAILURE', 'Bad isTyping.');
        }
    }
    _error(data) {
        var _a;
        const error = data;
        if (error && error.errorCode && error.errorMessage && error.endChat !== undefined) {
            this.session.chatInstance.emitError(error.errorCode, error.errorMessage);
            if (error.endChat) {
                this.session.chatInstance.emitStatusChange('', (_a = error.provider) !== null && _a !== void 0 ? _a : this.providerIdentifier, ConnectionState.DISCONNECTED, { state: ChatState.COMPLETE, endedBy: 'ERROR', errorCode: error.errorCode });
                this.closeWebsocket(true);
            }
        }
        else {
            this.session.chatInstance.emitError('MESSAGE_RECEIVE_FAILURE', 'Bad error.');
        }
    }
    _statusChange(data) {
        const statusChange = data;
        if (!statusChange || !statusChange.chatStatus || !statusChange.provider || statusChange.sessionId === undefined) {
            this.session.chatInstance.emitError('MESSAGE_RECEIVE_FAILURE', 'Bad statusChange.');
            return;
        }
        this.providerIdentifier = statusChange.provider;
        this.sessionId = statusChange.sessionId;
        const [connectionState, chatState] = this.session.chatInstance.getCurrentStates();
        if (connectionState === ConnectionState.CONNECTING &&
            chatState.state === ChatState.READY &&
            (statusChange.chatStatus === ChatState.ON_HOLD || statusChange.chatStatus === ChatState.ACTIVE)) {
            if (this.session.config.useMocks.mockStartChatFailNoFeatures || this.session.chatInstance.getChatFeatures() == undefined) {
                this.rejectStartChatPromise({
                    code: 'FEATURE_UPDATE_FAILURE',
                    message: 'No feature update received.',
                });
                this.closeWebsocket(true);
                return;
            }
            if (this.session.config.useMocks.mockConnectionFail) {
                return;
            }
            else {
                this.resolveStartChatPromise();
            }
        }
        else if (statusChange.chatStatus === ChatState.COMPLETE) {
            this.resolveCloseChatPromise();
            this.session.chatInstance.emitStatusChange(statusChange.sessionId, statusChange.provider, ConnectionState.DISCONNECTED, {
                state: statusChange.chatStatus,
                transferTo: statusChange.transferTo,
                endedBy: statusChange.endedBy,
                errorCode: statusChange.errorCode,
            });
            this.closeWebsocket(true);
            return;
        }
        else if (connectionState === ConnectionState.RECONNECTING &&
            (statusChange.chatStatus === ChatState.ON_HOLD || statusChange.chatStatus === ChatState.ACTIVE)) {
            if (this.session.config.useMocks.mockRestoreChatFail) {
                return;
            }
            else {
                this.endReconnection();
                this.resolveStartChatPromise();
            }
        }
        this.session.chatInstance.emitStatusChange(statusChange.sessionId, statusChange.provider, ConnectionState.CONNECTED, {
            state: statusChange.chatStatus,
            transferTo: statusChange.transferTo,
            endedBy: statusChange.endedBy,
            errorCode: statusChange.errorCode,
        });
    }
    _acknowledgement(data) {
        const acknowledgement = data;
        if (acknowledgement && acknowledgement.messageId) {
            this.messageAcknowledgementReceived(parseInt(acknowledgement.messageId));
        }
        else {
            this.session.chatInstance.emitError('MESSAGE_RECEIVE_FAILURE', 'Bad acknowledgement.');
        }
    }
    _notification(data) {
        const notification = data;
        if (notification && notification.type) {
            this.session.chatInstance.emitNotification(notification.type, new Date(), notification.url, notification.label, notification.content, notification.secondsRemaining ? parseInt(notification.secondsRemaining) : undefined);
        }
        else {
            this.session.chatInstance.emitError('NOTIFICATION_FAILURE');
        }
    }
}
//# sourceMappingURL=gateway-provider.js.map